import type { BlogListBlockProps, BlogPost } from '../types';
import { theme, sectionColors, cardGridClass, cssColor, cardTextColor } from '../theme';

/** Human date (UTC-safe-ish) for the post byline; blank string if unparseable. */
function formatDate(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

/**
 * Pure blog index. Renders PUBLISHED posts (host-provided from SiteConfig
 * content.blog_posts — authored in the Blog workspace, which also has the AI
 * writer). Each card links to the post detail page (/blog/:slug), which already
 * exists on the tenant site. Framework-agnostic; graceful empty state; never fetches.
 */
export function BlogListBlock({ props, posts }: { props: BlogListBlockProps; posts: BlogPost[] }) {
  const { heading, limit, layout = 'grid', showCover = true, showExcerpt = true } = props;
  const items = typeof limit === 'number' ? posts.slice(0, limit) : posts;
  const colors = sectionColors(props, { bg: 'transparent', text: theme.text });
  // The card's own colour (Card colour, else the site's Surface) and words readable on it.
  const cardBg = cssColor(props.cardBgColor);
  const cardLook = { backgroundColor: cardBg ?? theme.surface, color: cardTextColor(props, { cardBg, customCard: Boolean(cardBg) }) };

  if (items.length === 0) {
    return (
      <section className="px-6 py-12 text-center" style={{ ...colors, fontFamily: theme.bodyFont }}>
        <p className="opacity-60">No posts published yet.</p>
      </section>
    );
  }

  const grid = cardGridClass({ columns: props.columns, layout });

  return (
    <section className="px-6 py-12" style={{ ...colors, fontFamily: theme.bodyFont }}>
      {heading ? (
        <h2 className="mx-auto mb-8 max-w-6xl text-2xl font-semibold" style={{ fontFamily: theme.headingFont }}>
          {heading}
        </h2>
      ) : null}
      <div className={`mx-auto grid max-w-6xl gap-6 ${grid}`}>
        {items.map((p) => {
          const date = formatDate(p.published_at);
          const byline = [p.author, date].filter(Boolean).join(' · ');
          return (
            <a
              key={p.slug || p.title}
              href={`/blog/${encodeURIComponent((p.slug || '').replace(/^\//, ''))}`}
              className="blk-card block overflow-hidden rounded-lg border border-black/10 transition hover:shadow-md"
              style={cardLook}
            >
              {showCover && p.cover_image_url ? (
                <img src={p.cover_image_url} alt={p.cover_image_alt || p.title} referrerPolicy="no-referrer" loading="lazy" decoding="async" className="h-44 w-full object-cover" />
              ) : null}
              <div className="p-4">
                <div className="text-lg font-semibold" style={{ fontFamily: theme.headingFont }}>
                  {p.title}
                </div>
                {byline ? <div className="mt-1 text-xs opacity-60">{byline}</div> : null}
                {showExcerpt && p.excerpt ? (
                  <p className="mt-2 line-clamp-3 text-sm opacity-75">{p.excerpt}</p>
                ) : null}
                {p.tags && p.tags.length ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {p.tags.slice(0, 4).map((t) => (
                      <span
                        key={t}
                        className="rounded-full px-2 py-0.5 text-xs"
                        style={{ backgroundColor: theme.accent, color: theme.secondary }}
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                ) : null}
                <span className="blk-link mt-3 inline-block text-sm font-medium" style={{ color: theme.primary }}>
                  Read post →
                </span>
              </div>
            </a>
          );
        })}
      </div>
    </section>
  );
}
